import React, { useMemo } from 'react';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';

import LessonsEmptyState from '@/features/guardian-player/components/player-detail/lessons-empty-state';
import { LessonCard } from '@/features/guardian/components/lesson-carousel/card';

import type { PlayerDetailLesson } from '@/features/guardian-player/components/player-detail/types';

const strings = STRINGS.guardianPlayer.playerDetail.lessons;

type LessonsSectionProps = {
  playerFirstName: string;
  lessons: PlayerDetailLesson[];
  onPressViewDetail: (lesson: PlayerDetailLesson) => void;
  onPressReschedule: (lesson: PlayerDetailLesson) => void;
  onPressLocation: (lesson: PlayerDetailLesson) => void;
  onPressBookClass: () => void;
};

export default function LessonsSection({
  playerFirstName,
  lessons,
  onPressViewDetail,
  onPressReschedule,
  onPressLocation,
  onPressBookClass,
}: LessonsSectionProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();

  const rows = useMemo(() => {
    if (!isTablet) return lessons.map(l => [l]);
    const result = [];
    for (let i = 0; i < lessons.length; i += 2) {
      result.push(lessons.slice(i, i + 2));
    }
    return result;
  }, [lessons, isTablet]);

  return (
    <Stack gap={scale(8)}>
      <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.title}>
        {strings.title.replace('{name}', playerFirstName)}
      </Text>
      {lessons.length === 0 ? (
        <LessonsEmptyState onPressBookClass={onPressBookClass} />
      ) : (
        <Stack gap={scale(12)}>
          {rows.map((row, rowIndex) => (
            <Stack key={rowIndex} direction={isTablet ? 'row' : 'column'} gap={scale(12)}>
              <Stack flex={1}>
                <LessonCard
                  item={{
                    id: row[0].id,
                    label: strings.nextUp,
                    date: `${row[0].dayLabel} · ${row[0].dateLabel}`,
                    time: row[0].timeLabel,
                    academyName: row[0].academyName,
                    programName: `${row[0].sessionType} · ${row[0].bay}`,
                    coachName: row[0].coachName,
                    coachAvatar: row[0].coachAvatar,
                  }}
                  width={'100%' as any}
                  onViewDetailsPress={() => onPressViewDetail(row[0])}
                  onReschedulePress={() => onPressReschedule(row[0])}
                  onLocationPress={() => onPressLocation(row[0])}
                />
              </Stack>
              {isTablet &&
                (row[1] ? (
                  <Stack flex={1}>
                    <LessonCard
                      item={{
                        id: row[1].id,
                        label: strings.nextUp,
                        date: `${row[1].dayLabel} · ${row[1].dateLabel}`,
                        time: row[1].timeLabel,
                        academyName: row[1].academyName,
                        programName: `${row[1].sessionType} · ${row[1].bay}`,
                        coachName: row[1].coachName,
                        coachAvatar: row[1].coachAvatar,
                      }}
                      width={'100%' as any}
                      onViewDetailsPress={() => onPressViewDetail(row[1])}
                      onReschedulePress={() => onPressReschedule(row[1])}
                      onLocationPress={() => onPressLocation(row[1])}
                    />
                  </Stack>
                ) : (
                  <Stack flex={1} />
                ))}
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

const useStyles = createStyles(() => ({
  title: {
    color: guardianColors['10'],
  },
}));
